import React from 'react';

import { Column } from '@ant-design/plots';
import styled from 'styled-components';

import { CompanyAICICategoryAverage } from 'types/recommendations';

import { AI_CI_CATEGORIES } from './constants';

const PLOT_HEIGHT = 240;
const COLUMN_WIDTH = 16;
const AXIS_COLOR = '#222222';
const GRID_COLOR = '#f1f1f1';
const X_AXIS_LINE_COLOR = '#d9d9d9';

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const IconBox = styled.div`
  padding: 6px;
  border-radius: 4px;
  background-image: linear-gradient(to bottom, #f9f9ff, #ebebfe);
  line-height: 0;
`;

const Title = styled.div`
  font-size: 18px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const ChartArea = styled.div`
  height: ${PLOT_HEIGHT}px;
`;

const categoryLabel = (key: string): string => AI_CI_CATEGORIES.find((c) => c.key === key)?.label ?? key;

type Props = {
  data: CompanyAICICategoryAverage[];
};

const AICIAvgScoreChart = ({ data }: Props) => {
  const chartData = data.map((item) => ({ category: categoryLabel(item.category), average: item.average }));

  const config = {
    data: chartData,
    xField: 'category',
    yField: 'average',
    animation: false as const,
    autoFit: true,
    maxColumnWidth: COLUMN_WIDTH,
    columnBackground: { style: { fill: GRID_COLOR } },
    columnStyle: {
      fill: 'l(270) 0:#007aff 1:#9dcafc',
      stroke: '#007aff',
      lineWidth: 1,
    },
    yAxis: {
      min: 0,
      max: 100,
      tickInterval: 25,
      line: null,
      tickLine: null,
      grid: { line: { style: { stroke: GRID_COLOR, lineWidth: 1 } } },
      label: { style: { fontSize: 10, fill: AXIS_COLOR } },
    },
    xAxis: {
      line: { style: { stroke: X_AXIS_LINE_COLOR, lineWidth: 1 } },
      tickLine: null,
      label: { autoRotate: false, autoHide: false, autoEllipsis: false, style: { fontSize: 10, fill: AXIS_COLOR } },
    },
    label: false as const,
    legend: false as const,
    tooltip: {
      formatter: (datum: any) => ({
        name: 'Score',
        value: datum.average,
      }),
    },
  };

  return (
    <div>
      <Header>
        <IconBox>
          <img src="/icons/stacked-bar-chart-purple.svg" width={20} height={20} alt="" />
        </IconBox>
        <div>
          <Title>Avg Score Per Category</Title>
          <Subtitle>Average portfolio-wide score per each scoring category.</Subtitle>
        </div>
      </Header>
      <ChartArea>
        <Column {...config} />
      </ChartArea>
    </div>
  );
};

export default AICIAvgScoreChart;
